import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, filter, switchMap, take, timeout } from "rxjs/operators";
import { UserDataService } from "../user-data/user-data.service";

// How long the interceptor will wait, at app start, for MSAL to resolve a token
// before giving up and sending the request without one.
const TOKEN_WAIT_TIMEOUT_MS = 8000;

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private cache = new Map<string, HttpResponse<any>>();

  constructor(private userDataService: UserDataService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.method !== 'GET') {
      this.cache.clear();
    } else if (request.url.match(/sv=/)) {
      return next.handle(request);
    }

    return this.userDataService.accessToken$.pipe(
      // undefined means MSAL hasn't resolved a token yet - wait for it instead of
      // firing the request unauthenticated (avoids the startup race with MSAL init).
      filter((token) => token !== undefined),
      take(1),
      timeout(TOKEN_WAIT_TIMEOUT_MS),
      catchError(() => of(this.userDataService.getAccessToken() ?? null)),
      switchMap((accessToken) => {
        if (accessToken) {
          request = request.clone({
            setHeaders: {
              'X-Frame-Options': 'SAMEORIGIN',
              'X-Content-Type-Options': 'nosniff',
              'Strict-Transport-Security': 'max-age=31536000',
              'Content-Security-Policy': 'default-src \'self\'',
              'Referrer-Policy': 'same-origin',
              'Permissions-Policy': 'geolocation=(self)',
              'Cross-Origin-Embedder-Policy': 'require-corp',
              'Cross-Origin-Resource-Policy': 'same-origin',
              'Cross-Origin-Opener-Policy': 'same-origin',
              Authorization: `Bearer ${accessToken}`
            }
          });
        }
        return next.handle(request);
      })
    );
  }
}
